import { useCallback, useEffect, useReducer } from "react"

import type { TemplateInfo } from "./template-data"
import type { InterviewAnswers, WizardPath } from "./wizard-types"
import type { UseCaseFormData } from "../../types/use-case.types"

import { ArrowLeft, ArrowRight } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { AiGeneratingStep } from "./ai-generating-step"
import { AiInterviewStep } from "./ai-interview-step"
import { EntryPointCards } from "./entry-point-cards"
import { TemplatePicker } from "./template-picker"
import { initialWizardState, wizardReducer } from "./wizard-reducer"
import { WizardSummaryCard } from "./wizard-summary-card"
import { useAiGenerateFull } from "../../hooks/use-ai-generate-full"

type NewUseCaseWizardProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (data: UseCaseFormData) => void | Promise<void>
  initPath?: "ai" | "template" | null
}

export function NewUseCaseWizard({
  open,
  onOpenChange,
  onComplete,
  initPath,
}: NewUseCaseWizardProps) {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState)
  const generateMutation = useAiGenerateFull()

  // Reset wizard when dialog opens, optionally jumping to a path
  useEffect(() => {
    if (open) {
      dispatch({ type: "RESET" })
      if (initPath) {
        dispatch({ type: "SET_PATH", path: initPath })
      }
    }
  }, [open, initPath])

  const handlePathSelect = useCallback(
    async (path: WizardPath) => {
      if (path === "scratch") {
        try {
          await onComplete({} as UseCaseFormData)
          onOpenChange(false)
        } catch {
          // Keep dialog open on failure
        }
        return
      }
      dispatch({ type: "SET_PATH", path })
    },
    [onOpenChange, onComplete]
  )

  const handleAnswer = useCallback((field: keyof InterviewAnswers, value: string | string[]) => {
    dispatch({ type: "SET_ANSWER", field, value })
  }, [])

  const handleNextInterview = useCallback(() => {
    if (
      state.step === "interview-4" ||
      (state.step === "interview-3" && state.answers.problem && state.answers.solution)
    ) {
      // Move to generating step and trigger API call
      dispatch({ type: "NEXT_STEP" })
      if (state.step === "interview-4") {
        // Already at step 4, next is generating
        generateMutation.mutate(state.answers, {
          onSuccess: (data) => dispatch({ type: "SET_GENERATED", data }),
        })
      }
    } else {
      dispatch({ type: "NEXT_STEP" })
    }
  }, [state.step, state.answers, generateMutation])

  // When we land on "generating" step, fire the API call
  useEffect(() => {
    if (state.step === "generating" && !generateMutation.isPending && !state.generatedData) {
      generateMutation.mutate(state.answers, {
        onSuccess: (data) => dispatch({ type: "SET_GENERATED", data }),
      })
    }
  }, [state.step])

  const handleTemplateSelect = useCallback((template: TemplateInfo) => {
    dispatch({ type: "SELECT_TEMPLATE", templateId: template.id, data: template.data })
  }, [])

  const handleConfirm = useCallback(async () => {
    if (state.generatedData) {
      try {
        await onComplete(state.generatedData)
        onOpenChange(false)
      } catch {
        // Keep dialog open on failure — caller handles the error toast
      }
    }
  }, [state.generatedData, onComplete, onOpenChange])

  const handleBack = useCallback(() => {
    dispatch({ type: "PREV_STEP" })
  }, [])

  // Can proceed from interview steps?
  const canProceed =
    (state.step === "interview-1" && state.answers.problem.trim().length > 0) ||
    (state.step === "interview-2" && state.answers.users.trim().length > 0) ||
    (state.step === "interview-3" && state.answers.solution.trim().length > 0) ||
    state.step === "interview-4" // optional step, always can proceed

  const showBackButton = state.step !== "entry"
  const showNextButton =
    state.step === "interview-1" ||
    state.step === "interview-2" ||
    state.step === "interview-3" ||
    state.step === "interview-4"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New Use Case</DialogTitle>
          <DialogDescription>
            {state.step === "entry" && "Choose how you'd like to get started."}
            {state.step.startsWith("interview") && "AI Quick Start — answer a few questions."}
            {state.step === "generating" && "AI is drafting your use case..."}
            {state.step === "template" && "Pick a template to start from."}
            {state.step === "summary" && "Review before editing."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {state.step === "entry" && <EntryPointCards onSelect={handlePathSelect} />}

          {state.step.startsWith("interview") && (
            <AiInterviewStep answers={state.answers} step={state.step} onAnswer={handleAnswer} />
          )}

          {state.step === "generating" && <AiGeneratingStep />}

          {state.step === "template" && <TemplatePicker onSelect={handleTemplateSelect} />}

          {state.step === "summary" && state.generatedData && (
            <WizardSummaryCard
              data={state.generatedData}
              onBack={handleBack}
              onConfirm={handleConfirm}
            />
          )}
        </DialogBody>

        {(showBackButton || showNextButton) && state.step !== "summary" && (
          <DialogFooter>
            {showBackButton && state.step !== "generating" && (
              <Button variant="ghost" onClick={handleBack}>
                <ArrowLeft className="mr-1 size-4" />
                Back
              </Button>
            )}
            <div className="flex-1" />
            {showNextButton && (
              <Button disabled={!canProceed} onClick={handleNextInterview}>
                {state.step === "interview-4" ? "Generate" : "Next"}
                <ArrowRight className="ml-1 size-4" />
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
